<template>
  <div class="gateway-control">
    <div class="controls">
      <div class="update-interval-container">
        <label for="interval">Update Interval (seconds):</label>
        <input
          id="interval"
          type="number"
          v-model="updateInterval"
          @change="updateIntervalChange"
          min="5"
        />
      </div>
    </div>
    <div class="header-container">
      <h2>Gateway Control</h2>
      <div class="header-actions">
        <div class="server-status-bar">
          <span class="status-label">서버 상태:</span>
          <span :class="['status-badge', apiStatus]" :title="apiStatusMessage">
            {{ apiStatus === 'checking' ? '확인 중…' : apiStatus === 'ok' ? 'API 연결됨' : apiStatus === 'error' ? 'API 끊김' : '미확인' }}
          </span>
          <span :class="['status-badge', isConnected ? 'ws-ok' : 'ws-error']">
            {{ isConnected ? 'WS 연결됨' : 'WS 끊김' }}
          </span>
          <button type="button" class="status-check-btn" @click="checkServerStatus" :disabled="apiStatus === 'checking'">
            {{ apiStatus === 'checking' ? '확인 중…' : '상태 확인' }}
          </button>
          <span v-if="apiLastCheck" class="last-check">마지막 확인: {{ apiLastCheck }}</span>
        </div>
        <button class="refresh-btn" @click="getGWInformation">
          <span class="refresh-icon">↻</span>
          Get GW Info
        </button>
        <button
          class="batch-update-btn"
          :disabled="!canBatchUpdateFw0304 || batchUpdateInProgress"
          @click="startBatchUpdateFw0304"
        >
          {{ batchUpdateInProgress ? `업데이트 중 (${batchUpdateProgress})` : `0.0.0.3→0.0.0.4 일괄 (1분/1대)${gatewaysFw0304.length ? ` [${gatewaysFw0304.length}대]` : ''}` }}
        </button>
        <button
          v-if="batchUpdateInProgress"
          class="batch-cancel-btn"
          @click="batchUpdateCancel = true"
        >
          중지
        </button>
      </div>
    </div>

    <!-- 데이터가 있는지 확인하는 디버깅용 -->
    <div>Total Gateways: {{ connectedGateways.length }}</div>

    <div class="table-container">
      <table v-if="connectedGateways.length > 0">
        <thead>
          <tr>
            <th @click="sortGateways('devicename')">Device Name <span :class="getArrowClass('devicename')"></span></th>
            <th @click="sortGateways('gw_mac')">gw_mac <span :class="getArrowClass('gw_mac')"></span></th>
            <th @click="sortGateways('hwVer')">H/W Version <span :class="getArrowClass('hwVer')"></span></th>
            <th @click="sortGateways('fwVer')">F/W Version <span :class="getArrowClass('fwVer')"></span></th>
            <!-- <th @click="sortGateways('otaServerUrl')">OTA Server URL <span :class="getArrowClass('otaServerUrl')"></span></th> -->
            <th @click="sortGateways('wsUrl')">WS Server URL <span :class="getArrowClass('wsUrl')"></span></th>
            <th @click="sortGateways('reportInterval')">Report Interval <span :class="getArrowClass('reportInterval')"></span></th>
            <th @click="sortGateways('rssiFilter')">RSSI Scan Range <span :class="getArrowClass('rssiFilter')"></span></th>
            <th @click="sortGateways('active')">Status <span :class="getArrowClass('active')"></span></th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="gateway in connectedGateways" :key="gateway.gwMac">
            <td>
              <span :class="{ 'connected': gateway.active === 0, 'disconnected': gateway.active !== 0 }">
                {{ gateway.devicename }}
              </span>
            </td>
            <td>{{ gateway.gw_mac }}</td>
            <td>{{ gateway.hwVer }}</td>
            <td>{{ gateway.fwVer }}</td>
            <!-- <td>{{ gateway.otaServerUrl }}</td> -->
            <td>{{ gateway.wsUrl }}</td>
            <td>{{ gateway.reportInterval }}</td>
            <td>{{ gateway.rssiFilter }}</td>
            <td>{{ gateway.active === 0 ? 'Connected' : 'Disconnected' }}</td>
            <td>
              <button
                v-if="(gateway.connect === 0 || $isadmin === 1) && gateway.ip && gateway.port"
                class="edit-btn"
                :class="{ 'edit-btn-warn': gateway.connect !== 0 && $isadmin === 1 }"
                :title="gateway.connect !== 0 && $isadmin === 1 ? 'WS 미연결 GW — admin 강제 진입 (명령 전송이 실패할 수 있음)' : ''"
                @click="openEditModal(gateway)">
                Settings
              </button>
              <span v-else-if="!gateway.ip || !gateway.port" class="disconnected-text">
                No Connection Info
              </span>
            </td>
          </tr>
        </tbody>
      </table>
      <div v-if="showEditModal" class="modal-overlay">
        <div class="modal-content">
          <div class="modal-header">
            <h3>Gateway Settings</h3>
            <span class="close-button" @click="closeEditModal">&times;</span>
          </div>
          
          <div v-if="editingGateway" class="modal-body">
            <div class="form-group">
              <label>(Manual) OTA Server URL:</label>
              <div class="input-group">
                <select v-model="selectedOtaUrl" class="setting-select">
                  <option v-for="url in otaUrls" :key="url.id || url.url" :value="url">
                    {{ url.url }}
                    {{ (url.isMain === true || url.isMain === 1 || url.is_main === 1) ? '(Main)' : '' }}
                  </option>
                </select>
                <button
                  type="button"
                  class="ota-check-btn"
                  :disabled="otaCheckLoading || !currentOtaUrlForCheck"
                  @click="checkOtaServer"
                >
                  {{ otaCheckLoading ? '확인 중…' : '동작 확인' }}
                </button>
                <button
                  @click="applyOtaUrl"
                  class="apply-btn"
                  :disabled="isApplying"
                >
                  {{ isApplying ? 'Applying...' : 'Apply' }}
                </button>
                <button
                  @click="applyOtaUrlToAll"
                  class="apply-btn apply-all-btn"
                  :disabled="isApplying"
                >
                  Apply to All Connected
                </button>
              </div>
              <div v-if="otaCheckResult !== null" class="ota-check-result" :class="otaCheckResult.ok ? 'ok' : 'fail'">
                {{ otaCheckResult.ok ? 'OTA 서버 연결됨' : (otaCheckResult.message || 'OTA 서버 연결 실패') }}
              </div>
            </div>

            <div class="form-group">
              <label>Report Interval (seconds):</label>
              <div class="input-group">
                <input v-model="editingGateway.reportInterval" type="number" min="1" max="21600" class="setting-input">
                <button @click="applyReportInterval" class="apply-btn" :disabled="isApplying">
                  {{ isApplying ? 'Applying...' : 'Apply' }}
                </button>
              </div>
              <div class="interval-range">
                <small>Scan range: 1 sec ~ 6 hours(21600 sec)</small>
              </div>
            </div>
            <div class="form-group">
              <label>Tag Scan Range (RSSI):</label>
              <div class="rssi-container">
                <div class="circle-slider-container">
                  <div class="circle-visualization" 
                      :style="{ 
                        width: circleSize + 'px', 
                        height: circleSize + 'px',
                        backgroundColor: 'rgba(33, 150, 243, 0.2)',
                        border: '2px solid #2196F3'
                      }">
                    <span class="current-value">{{ editingGateway.rssiFilter }} dBm</span>
                  </div>
                  <input 
                    type="range" 
                    v-model="editingGateway.rssiFilter" 
                    min="-100" 
                    max="-10" 
                    step="1"
                    class="circle-range"
                    style="direction: rtl;"
                    @input="updateCircleSize"
                  >
                </div>
                <div class="rssi-labels">
                  <span class="rssi-min">-10 dBm</span>
                  <span class="rssi-max">-100 dBm</span>
                </div>
                <div class="rssi-actions">
                  <button @click="applyRssiFilter" class="apply-btn" :disabled="isApplying">
                    {{ isApplying ? 'Applying...' : 'Apply' }}
                  </button>
                  <button
                    @click="applyRssiFilterToAll"
                    class="apply-btn apply-all-btn"
                    :disabled="isApplying"
                    title="회사의 라이브 WS 연결된 모든 GW 에 동일 RSSI 임계값을 일괄 전송"
                  >
                    {{ isApplying ? 'Applying...' : 'Apply to All Connected' }}
                  </button>
                </div>
              </div>
            </div>


            <!-- <div class="form-group">
              <label>Tag Scan Range (RSSI):</label>
              <div class="rssi-container">
                <input 
                  v-model="editingGateway.rssiFilter" 
                  type="range" 
                  min="-100" 
                  max="-10" 
                  step="-1"
                  class="rssi-slider"
                  style="direction: rtl;"
                >
                <div class="rssi-labels">
                  <span class="rssi-min">-10 dBm</span>
                  <span class="current-value">Current Value: {{ editingGateway.rssiFilter }} dBm</span>
                  <span class="rssi-max">-100 dBm</span>
                </div>
                <button @click="applyRssiFilter" class="apply-btn">Apply</button>
              </div>
            </div> -->
          <div class="modal-footer">
            <button class="cancel-btn" @click="closeEditModal">Close</button>
          </div>
        </div>
      </div>
      
    </div>
  </div>
  </div>  
</template>

<script>
import axios from 'axios';

export default {
  name: 'GatewayList',
  data() {
    return {
      ws: null,
      updateInterval: 10,
      updateTimer: null,
      isConnected: false,
      connectedGateways: [],
      selectedGateway: null,
      gatewayList: [],
      showEditModal: false,
      // editingGateway: null,
      otaUrls: [],
      selectedOtaUrl: null,
      reportInterval: '',
      lastFetchTime: 0,
      fetchDebounceTimer: null,
      rssiFilter: '',
      isPaused: false,
      connect: null,
      isApplying: false,
      otaResponseReceived: false, // OTA 응답 수신 여부 추적
      lastOtaResponseTime: 0, // 마지막 OTA 응답 시간
      isFetchingGatewayList: false, // fetchMainGatewayList 실행 중 여부
      lastSetOtaUrlResponseTime: 0, // 마지막 0x02 응답 시간
      setOtaUrlResponseReceived: false, // 0x02 응답 수신 여부
      circleSize: 200,
      editingGateway:{
        rssiFilter: 0,
      },
      sortKey: 'devicename',
      sortOrder: 1, // 1 for ascending, -1 for descending
      apiStatus: 'idle', // 'idle' | 'checking' | 'ok' | 'error'
      apiStatusMessage: '',
      apiLastCheck: null,
      otaCheckLoading: false,
      otaCheckResult: null, // { ok: boolean, message?: string }
      batchUpdateInProgress: false,
      batchUpdateProgress: '', // e.g. "3/10"
      batchUpdateCancel: false,
      // RSSI / Report Interval apply 의 WS 응답 타임아웃 핸들
      rssiApplyTimeout: null,
      reportIntervalTimeout: null,
    }
  },
  computed: {
    gatewaysFw0304() {
      return this.connectedGateways.filter(gw =>
        gw.connect === 0 &&
        gw.ip &&
        gw.port &&
        String(gw.fwVer || '').trim() === '0.0.0.3'
      );
    },
    canBatchUpdateFw0304() {
      return (
        this.gatewaysFw0304.length > 0 &&
        this.currentOtaUrlForCheck &&
        !this.isApplying
      );
    },
    currentOtaUrlForCheck() {
      const v = this.selectedOtaUrl;
      if (!v) return '';
      return typeof v === 'string' ? v : (v?.url || '');
    },
  },
  watch: {
    otaUrls: {
      immediate: true, // 컴포넌트 생성 시 즉시 실행
      handler(newUrls) {
        if (newUrls.length > 0) {
          // isMain이 true이거나 1인 URL을 찾아서 기본값으로 설정
          const mainUrl = newUrls.find(url => 
            url.isMain === true || url.isMain === 1 || url.is_main === 1
          );
          if (mainUrl) {
            this.selectedOtaUrl = mainUrl;
          }
        }
      }
    }
  },
  mounted() {
    // company가 없으면 초기화하지 않음
    const company = localStorage.getItem('company');
    if (!company) {
      console.warn('Company not found in localStorage, skipping GatewayCountrol2 initialization');
      return;
    }
    
    this.fetchMainGatewayList();
    this.fetchOtaUrls();
    this.setupWebSocket();
    const savedInterval = localStorage.getItem('updateInterval');
    if (savedInterval) {
     this.updateInterval = parseInt(savedInterval);
    }
  
  // 초기 타이머 설정
  this.updateIntervalChange(); // 7000ms = 7초
    this.checkServerStatus();
  },
  beforeUnmount() {
    if (this.updateTimer) {
      clearInterval(this.updateTimer);
      this.updateTimer = null;
    }
    if (this.fetchDebounceTimer) {
      clearTimeout(this.fetchDebounceTimer);
      this.fetchDebounceTimer = null;
    }
  },
  methods: {
    async checkServerStatus() {
      this.apiStatus = 'checking';
      this.apiStatusMessage = '';
      try {
        const res = await axios.get('/api/health', { timeout: 8000 });
        this.apiStatus = res.data?.status === 'healthy' ? 'ok' : 'error';
        this.apiStatusMessage = res.data?.status === 'healthy'
          ? `업타임 ${res.data?.uptime?.formatted || '-'}`
          : (res.data?.message || '응답 이상');
        this.apiLastCheck = new Date().toLocaleTimeString('ko-KR');
      } catch (err) {
        this.apiStatus = 'error';
        this.apiStatusMessage = err.message || err.code || '연결 실패';
        this.apiLastCheck = new Date().toLocaleTimeString('ko-KR');
      }
    },
    async checkOtaServer() {
      const url = this.currentOtaUrlForCheck;
      if (!url) return;
      this.otaCheckLoading = true;
      this.otaCheckResult = null;
      try {
        const res = await axios.get('/api/ota-servers/check', {
          params: { url },
          timeout: 10000,
        });
        this.otaCheckResult = {
          ok: !!res.data?.ok,
          message: res.data?.message,
        };
      } catch (err) {
        this.otaCheckResult = {
          ok: false,
          message: err.response?.data?.message || err.message || '연결 확인 실패',
        };
      } finally {
        this.otaCheckLoading = false;
      }
    },
    updateCircleSize() {
      if (!this.editingGateway) return;
      // RSSI 값(-100 ~ -10)을 원 크기(50px ~ 300px)로 변환
      const value = parseInt(this.editingGateway.rssiFilter);
      const minSize = 100;
      const maxSize = 200;
      const normalized = (-value - 10) / 90; // -100 ~ -10 를 0 ~ 1 로 정규화
      this.circleSize = minSize + ((1 - normalized) * (maxSize - minSize));
    },

    setEditingGateway(gateway) {
      this.editingGateway = { ...gateway };
      this.$nextTick(() => {
        this.updateCircleSize();  // 게이트웨이 정보가 설정된 후 원 크기 업데이트
      });
    },
    watch: {
      'editingGateway.rssiFilter' : {
        immediate: true,
        handler() {
          this.$nextTick(() => {
            this.updateCircleSize();
          });
        }
      }
    },
    updateIntervalChange() {
    // 이전 타이머 제거
    if (this.updateTimer) {
      clearInterval(this.updateTimer);
      this.updateTimer = null;
    }

    // 최소값 체크
    if (this.updateInterval < 5) {
      this.updateInterval = 5;
      alert('최소 업데이트 간격은 5초입니다.');
    }

    // company가 없으면 타이머 설정하지 않음
    const company = localStorage.getItem('company');
    if (!company) {
      console.warn('Company not found in localStorage, skipping updateIntervalChange');
      return;
    }

    // 새로운 타이머 설정
    this.updateTimer = setInterval(() => {
      // company가 없으면 요청하지 않음
      const currentCompany = localStorage.getItem('company');
      if (!currentCompany) {
        console.warn('Company not found in localStorage, skipping fetchMainGatewayList');
        return;
      }

      // OTA 업데이트 진행 중이거나 Settings 모달 열려있으면 건너뛰기
      // (모달 안에서 RSSI/Report Interval 편집 중 화면이 갱신되어 입력이 끊기는 문제 방지)
      if (!this.isApplying && !this.isPaused && !this.showEditModal) {
        this.fetchMainGatewayList();
      }
    }, this.updateInterval * 1000);

    // 로컬 스토리지에 저장
    localStorage.setItem('updateInterval', this.updateInterval);
    },
    debouncedFetchMainGatewayList() {
      // OTA 업데이트 진행 중 / Settings 모달 열림 / 직전 OTA 응답 5초 이내면 억제
      const timeSinceOtaResponse = Date.now() - this.lastOtaResponseTime;
      if (this.isApplying || this.isPaused || this.showEditModal || (this.otaResponseReceived && timeSinceOtaResponse < 5000)) {
        return;
      }

      // 기존 타이머 취소
      if (this.fetchDebounceTimer) {
        clearTimeout(this.fetchDebounceTimer);
      }

      // 최소 2초 간격으로만 호출
      const now = Date.now();
      const timeSinceLastFetch = now - this.lastFetchTime;
      const minInterval = 2000; // 2초

      if (timeSinceLastFetch >= minInterval) {
        // 즉시 호출 가능
        this.fetchMainGatewayList();
        this.lastFetchTime = now;
      } else {
        // debounce: 남은 시간만큼 대기 후 호출
        const delay = minInterval - timeSinceLastFetch;
        this.fetchDebounceTimer = setTimeout(() => {
          // 다시 확인 (OTA 진행 중 / 모달 열림일 수 있음)
          if (!this.isApplying && !this.isPaused && !this.showEditModal) {
            this.fetchMainGatewayList();
            this.lastFetchTime = Date.now();
          }
        }, delay);
      }
    },
    async fetchMainGatewayList() {
      // 이미 요청이 진행 중이면 중복 호출 방지
      if (this.isFetchingGatewayList) {
        console.log('fetchMainGatewayList: 이미 진행 중이므로 건너뛰기');
        return;
      }
      
      // OTA 업데이트 진행 중 / Settings 모달 열림 / 직전 OTA 응답 5초 이내면 억제
      const timeSinceOtaResponse = Date.now() - this.lastOtaResponseTime;
      if (this.isPaused || this.isApplying || this.showEditModal || (this.otaResponseReceived && timeSinceOtaResponse < 5000)) {
        return;
      }

      // 요청 시작 플래그 설정
      this.isFetchingGatewayList = true;

      try {
        const company = localStorage.getItem('company');
        console.log('Fetching for company:', company);
        
        // company가 없으면 요청하지 않음
        if (!company) {
          console.warn('Company not found in localStorage, skipping fetchMainGatewayList');
          return;
        }

        const response = await axios.get('/api/gateway/getWsList', {
          params: { company: company }
        });

        console.log('Gateway response:', response.data);

        // 기존 게이트웨이 정보 유지하면서 업데이트
        const updatedGateways = response.data.map(gw => {
          // null 문자 제거 함수
          const cleanString = (str) => {
            return str ? str.replace(/\0/g, '') : null;
          };
          
          // ip와 port가 없으면 disconnected로 처리
          const hasConnectionInfo = gw.ip && gw.port;
          const connectStatus = hasConnectionInfo ? Number(gw.connect) : 1; // 1 = disconnected
          // online 표시는 gw_recent_web.active 기준 (0 = 정상 수신, -1 = 끊김)
          const activeStatus = (gw.active === 0 || gw.active === -1) ? gw.active : -1;

          return {
            ...gw, // 기존 정보 유지
            devicename: gw.devicename,
            gw_mac: gw.gw_mac,
            hwVer: cleanString(gw.hw_version),
            fwVer: cleanString(gw.fw_version),
            otaServerUrl: cleanString(gw.ota_server_url),
            wsUrl: cleanString(gw.ws_server_url),
            reportInterval: gw.report_interval,
            rssiFilter: gw.rssi_filter,
            ip: gw.ip || null,
            port: gw.port || null,
            isConnected: hasConnectionInfo && connectStatus === 0,
            connect: connectStatus,
            active: activeStatus
          };
        });
        console.log('gw.otaServerUrl:', response.data.ota_server_url);
        this.connectedGateways = updatedGateways;
        //await this.checkws();
      } catch (error) {
        console.error('Failed to fetch gateway list:', error);
      } finally {
        // 요청 완료 후 플래그 리셋 (성공/실패 관계없이)
        this.isFetchingGatewayList = false;
      }
    },
    async setupWebSocket() {
    const company = localStorage.getItem('company');
    const wsUrl = this.$wsUrl;
    this.ws = new WebSocket(wsUrl, ['web-client']);

    this.ws.onopen = () => {
      console.log('WebSocket connected');
      this.isConnected = true;
      this.ws.send(JSON.stringify({
        type: 'init',
        company: company
      }));
    };

    this.ws.onmessage = async (event) => {
      try {
        const data = JSON.parse(event.data);
        console.log('Received websocket message:', data);

        if (data.type === 'gateway_connected') {
          // 게이트웨이 연결 - OTA 진행 중 / 모달 열림이 아니면 업데이트
          if (!this.isApplying && !this.isPaused && !this.showEditModal) {
            await this.fetchMainGatewayList();
          }
        } else if (data.type === 'gateway_info_update') {
          console.log('gateway_info_update:', data);
          // debounce 적용: 최소 2초 간격으로만 호출 (OTA 진행 중이면 건너뛰기)
          this.debouncedFetchMainGatewayList();
        } else if (data.type === 'gateway_disconnected') {
          // 게이트웨이 연결 해제
        } else if (data.type === 'gateway_control') {
          console.log('Gateway control message:', data);
          // 명령 결과에 따른 알림 표시
          if (data.command === 'setOtaUrl') {
            // 0x02 응답 처리 - OTA URL 설정 결과
            // 중복 응답 방지: 같은 게이트웨이에서 3초 이내에 중복 응답이 오면 무시
            const now = Date.now();
            const timeSinceLastResponse = now - this.lastSetOtaUrlResponseTime;
            const isDuplicate = this.setOtaUrlResponseReceived && 
                                timeSinceLastResponse < 3000 && 
                                data.ip === this.editingGateway?.ip && 
                                data.port === this.editingGateway?.port;
            
            if (isDuplicate) {
              console.log('중복 0x02 응답 무시:', data);
              return;
            }
            
            // 응답 수신 플래그 설정
            this.setOtaUrlResponseReceived = true;
            this.lastSetOtaUrlResponseTime = now;
            
            // 팝업을 띄우지 않음 (0x07 응답을 기다림)
            console.log('0x02 (setOtaUrl) 응답 받음:', data);
            if (data.result === 'success' || data.success) {
              console.log('0x02 응답 성공:', data.message);
              // 성공해도 팝업을 띄우지 않음 (0x07 응답을 기다림)
              // 모달도 닫지 않음 (0x07 응답 후 닫음)
            } else {
              console.error('0x02 응답 실패:', data.message);
              alert('OTA Server URL 설정 실패: ' + (data.message || '알 수 없는 오류'));
              this.isApplying = false;
              this.isPaused = false;
              this.otaResponseReceived = false;
              this.setOtaUrlResponseReceived = false;
            }
            // setOtaUrl은 isApplying을 false로 설정하지 않음 (0x07을 기다리므로)
          } else if (data.command === 'setReportInterval') {
            console.log('Report interval response:', data);
            if (this.reportIntervalTimeout) {
              clearTimeout(this.reportIntervalTimeout);
              this.reportIntervalTimeout = null;
            }
            if (data.result === 'success') {
              alert(data.message);
              this.closeEditModal();
            } else {
              alert(data.message);
            }
            this.isApplying = false;
          } else if (data.command === 'setRssiFilter') {
            if (this.rssiApplyTimeout) {
              clearTimeout(this.rssiApplyTimeout);
              this.rssiApplyTimeout = null;
            }
            if (data.result === 'success') {
              alert(data.message);
              console.log('RSSI Filter response:', data);
              this.closeEditModal();
            } else {
              alert(data.message);
            }
            this.isApplying = false;
          } else if (data.command === 'cmdOtaStart') {
            console.log('cmdOtaStart WebSocket response:', data);
            
            // 중복 응답 방지: 같은 게이트웨이에서 3초 이내에 중복 응답이 오면 무시
            const now = Date.now();
            const timeSinceLastResponse = now - this.lastOtaResponseTime;
            const isDuplicate = this.otaResponseReceived && 
                                timeSinceLastResponse < 3000 && 
                                data.ip === this.editingGateway?.ip && 
                                data.port === this.editingGateway?.port;
            
            if (isDuplicate) {
              console.log('중복 OTA 응답 무시:', data);
              return;
            }
            
            // 응답 수신 플래그 설정
            this.otaResponseReceived = true;
            this.lastOtaResponseTime = now;
            
            // 타임아웃 취소
            if (this.otaResponseTimeout) {
              clearTimeout(this.otaResponseTimeout);
              this.otaResponseTimeout = null;
            }
            
            // 즉시 상태 초기화하여 추가 요청 방지
            this.isApplying = false;
            this.isPaused = false;
            
            // result 필드 확인 (success/fail 또는 success boolean)
            const isSuccess = data.result === 'success' || (data.result !== 'fail' && data.returnValue === '00');
            if (isSuccess) {
              alert(data.message || 'Command OTA Start success');
              console.log('cmdOtaStart 성공:', data);
              this.closeEditModal();
            } else {
              // 상세한 에러 메시지 표시
              const errorMsg = data.message || `Command OTA Start failed (returnValue: 0x${data.returnValue || 'unknown'})`;
              console.error('OTA Start failed:', errorMsg, data);
              alert(errorMsg);
            }
            
            // 5초 후 응답 플래그 리셋 (게이트웨이 안정화 시간 + 다음 OTA 시도 허용)
            // 이 시간 동안은 fetchMainGatewayList 호출이 억제됨
            setTimeout(() => {
              this.otaResponseReceived = false;
              console.log('OTA 응답 후 요청 억제 해제');
            }, 5000);
          } else if (data.command === 'cmdGwFactoryReset') {
            if (data.result === 'success') {
              this.closeEditModal();
            } else {
              alert(data.message);
            }
            this.isApplying = false;
            } 
        }
      } catch (error) {
        console.error('WebSocket message processing error:', error);
      }
    };

    this.ws.onclose = () => {
      console.log('WebSocket disconnected');
      this.isConnected = false;
      // 재연결 시도
      setTimeout(() => this.setupWebSocket(), 5000);
    };
  },
  async checkws() {
      if (this.isPaused) return;

      try {
        for (const gateway of this.connectedGateways) {
          const response = await axios.post('/api/checkactive/ws', {
            gw_mac: gateway.gw_mac
          });
          console.log('checkws response:', response.data);
          // 연결 상태 업데이트
          const index = this.connectedGateways.findIndex(g => g.gw_mac === gateway.gw_mac);
          if (index !== -1) {
            // Vue의 반응성을 위해 직접 객체 속성 업데이트
            this.connectedGateways[index].connect = response.data.connect;
            this.connectedGateways[index].isConnected = Number(response.data.connect) === 0;
        
            console.log(`Gateway ${gateway.gw_mac} response:`, response.data);
            console.log(`Gateway ${gateway.gw_mac} updated:`, {
              connect: this.connectedGateways[index].connect,
              isConnected: this.connectedGateways[index].isConnected
            });
           
          }
        }
      } catch (error) {
        console.error('Error fetching gateway information:', error);
      }
    },
    async getGWInformation() {
      try {
        const company = localStorage.getItem('company');
        console.log('Requesting GW info for company:', company);
        
        // 1. API 호출하여 게이트웨이에 0x01 메시지 전송
        const response = await axios.post('/api/gateway/request-info', {
          company: company
        });
        
        console.log('GW Info request response:', response.data);
        
        // 2. 게이트웨이가 응답할 시간을 기다림
        await new Promise(resolve => setTimeout(resolve, 3000));
        
        // 3. 업데이트된 게이트웨이 목록 가져오기
        await this.fetchMainGatewayList();
        
        // 4. 성공 메시지 표시
        if (response.data.success) {
          alert(`GW Info request sent to ${response.data.sentCount} gateways`);
        } else {
          alert('Failed to send GW Info request: ' + response.data.message);
        }
        
      } catch (error) {
        console.error('Error requesting gateway information:', error);
        alert('Error requesting gateway information: ' + error.message);
      }
    },

    openEditModal(gateway) {
      console.log('Opening modal for gateway:', gateway);
      console.log('Gateway ip:', gateway.ip, 'port:', gateway.port);
      
      // ip와 port가 없으면 경고 및 모달 열지 않음
      if (!gateway.ip || !gateway.port) {
        console.error('Gateway ip or port is missing:', gateway);
        alert('게이트웨이 IP 또는 포트 정보가 없습니다. 게이트웨이가 실제로 연결되지 않았을 수 있습니다. 게이트웨이 연결 상태를 확인해주세요.');
        return;
      }
      
      // 연결 상태 확인 — admin 은 우회 (명령 전송 시도는 가능, GW 응답은 별개)
      if (gateway.connect !== 0) {
        if (this.$isadmin === 1) {
          console.warn(`admin override: opening modal on disconnected GW ${gateway.gw_mac} (connect=${gateway.connect})`);
        } else {
          alert('게이트웨이가 연결되지 않았습니다. 연결 상태를 확인해주세요.');
          return;
        }
      }
      
      // 새 모달 진입 시점에 직전 시도의 stuck 상태 강제 회복
      this.isApplying = false;
      this.isPaused = false;
      this.otaResponseReceived = false;
      this.setOtaUrlResponseReceived = false;

      this.editingGateway = {
        gw_mac: gateway.gw_mac,
        otaServerUrl: gateway.otaServerUrl || '',
        reportInterval: gateway.reportInterval,
        rssiFilter: gateway.rssiFilter,
        ip: gateway.ip,
        port: gateway.port
      };
      this.checkConnectionStatus(gateway);
      this.showEditModal = true;
      
      console.log('Selected OTA URL:', this.selectedOtaUrl);
      console.log('Gateway OTA URL:', gateway.otaServerUrl);
      console.log('Editing gateway:', this.editingGateway);
    },
    checkConnectionStatus(gateway) {
      // 웹소켓 연결 상태 확인
      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        // 게이트웨이의 연결 상태를 확인하는 로직
        this.isConnected = this.connectedGateways.some(g => 
          g.ip === gateway.ip && g.port === gateway.port
        );
      } else {
        this.isConnected = false;
      }
    },

    closeEditModal() {
      this.showEditModal = false;
      this.editingGateway = null;
      // stuck 상태 회복 — WS 응답이 영영 안 오는 케이스에서 재시도 가능하도록
      this.isApplying = false;
      this.isPaused = false;
      this.otaResponseReceived = false;
      this.setOtaUrlResponseReceived = false;
      if (this.otaResponseTimeout) {
        clearTimeout(this.otaResponseTimeout);
        this.otaResponseTimeout = null;
      }
      if (this.rssiApplyTimeout) {
        clearTimeout(this.rssiApplyTimeout);
        this.rssiApplyTimeout = null;
      }
      if (this.reportIntervalTimeout) {
        clearTimeout(this.reportIntervalTimeout);
        this.reportIntervalTimeout = null;
      }
    },
    openSettings() {
      this.fetchOtaUrls();
    },
    async fetchOtaUrls() {
      try {
        const response = await fetch('/api/ota-servers', {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          }
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const contentType = response.headers.get('content-type');
        if (!contentType || !contentType.includes('application/json')) {
          throw new TypeError("The response is not in JSON format!");
        }

        const data = await response.json();
        console.log('OTA 서버 API 응답:', data);

        // 응답 형식에 따라 데이터 추출
        let servers = [];
        if (Array.isArray(data)) {
          servers = data;
        } else if (data && Array.isArray(data.data)) {
          // { success, message, data: [...], pagination } 형식
          servers = data.data;
        } else {
          console.error('Server response is not in array format:', data);
          this.otaUrls = [];
          return;
        }

        this.otaUrls = servers;
        console.log('설정된 OTA 서버 목록:', this.otaUrls);
        
        // 메인 서버가 있으면 기본 선택 (isMain이 true이거나 1인 경우)
        const mainServer = servers.find(server => 
          server.isMain === true || server.isMain === 1 || server.is_main === 1
        );
        if (mainServer) {
          this.selectedOtaUrl = mainServer;
        }
      } catch (error) {
        console.error('Get OTA Server List fail:', error);
        this.otaUrls = [];
        // 사용자에게 오류 메시지 표시
        this.$emit('error', 'Get OTA Server List fail');
      }
    },
    async applyOtaUrl() {
      try {
        // 이미 OTA 진행 중이면 중복 요청 방지
        if (this.isApplying) {
          console.log('OTA 이미 진행 중입니다.');
          return;
        }
        
        this.isPaused = true;
        this.isApplying = true;
        this.otaResponseReceived = false; // 응답 플래그 초기화
        this.setOtaUrlResponseReceived = false; // 0x02 응답 플래그 초기화
        this.lastSetOtaUrlResponseTime = 0; // 0x02 응답 시간 초기화
        
        // selectedOtaUrl이 객체인 경우 URL 추출
        const otaUrl = typeof this.selectedOtaUrl === 'string' 
          ? this.selectedOtaUrl 
          : (this.selectedOtaUrl?.url || this.selectedOtaUrl);
        
        console.log('Applying OTA URL:', otaUrl);
        
        // 프로토콜 순서: 1단계 - 0x02로 OTA Server URL 설정
        console.log('Step 1: Setting OTA Server URL (0x02)...');
        console.log('editingGateway:', this.editingGateway);
        console.log('ip:', this.editingGateway?.ip, 'port:', this.editingGateway?.port);
        
        if (!this.editingGateway || !this.editingGateway.ip || !this.editingGateway.port) {
          alert('게이트웨이 정보가 올바르지 않습니다. 게이트웨이를 다시 선택해주세요.');
          this.isApplying = false;
          this.isPaused = false;
          return;
        }
        
        const setUrlResponse = await axios.post('/api/gateway/setOTA', {
          selectedOtaUrl: otaUrl,
          ip: this.editingGateway.ip,
          port: this.editingGateway.port
        });
        console.log('setOTA HTTP response:', setUrlResponse.data);
        
        if (!setUrlResponse.data.success) {
          alert('OTA Server URL 설정 실패: ' + (setUrlResponse.data.message || '알 수 없는 오류'));
          this.isApplying = false;
          this.isPaused = false;
          this.otaResponseReceived = false;
          return;
        }
        
        // 0x02 응답을 기다림 (최대 5초)
        // WebSocket onmessage에서 setOtaUrl 명령을 처리하므로 별도 처리 불필요
        // 단순히 짧은 대기 시간만 추가 (게이트웨이가 URL을 처리할 시간)
        await new Promise((resolve) => {
          setTimeout(() => {
            console.log('0x02 응답 대기 완료, 0x07 전송 준비');
            resolve();
          }, 1000); // 1초 대기 (게이트웨이가 URL을 저장할 시간)
        });
        
        // 2단계 - 0x07로 OTA Start (URL 포함)
        console.log('Step 2: Starting OTA (0x07)...');
        const cmdOtaResponse = await axios.post('/api/gateway/cmdOTA', {
          gw_mac: this.editingGateway.gw_mac,
          selectedOtaUrl: otaUrl,
          ip: this.editingGateway.ip,
          port: this.editingGateway.port
        });
        console.log('cmdOTA HTTP response:', cmdOtaResponse.data);

        // HTTP 응답은 메시지 전송 성공만 의미함
        // 실제 결과는 WebSocket을 통해 게이트웨이 응답을 받아야 함
        if (!cmdOtaResponse.data.success) {
          alert('OTA Start 명령 전송 실패: ' + (cmdOtaResponse.data.message || '알 수 없는 오류'));
          this.isApplying = false;
          this.isPaused = false;
          this.otaResponseReceived = false;
          return;
        }
        
        // WebSocket 응답을 기다림 (최대 10초)
        const timeout = setTimeout(() => {
          if (this.isApplying && !this.otaResponseReceived) {
            console.warn('OTA 응답 타임아웃');
            alert('게이트웨이 응답을 받지 못했습니다. 게이트웨이 상태를 확인해주세요.');
            this.isApplying = false;
            this.isPaused = false;
            this.otaResponseReceived = false;
          }
        }, 10000);
        
        // WebSocket 응답이 오면 타임아웃 취소 (onmessage에서 처리)
        this.otaResponseTimeout = timeout;
      } catch (error) {
        console.error('OTA URL apply fail:', error);
        alert('OTA 명령 전송 중 오류가 발생했습니다: ' + error.message);
        this.isApplying = false;
        this.isPaused = false;
      }
    },
    async applyOtaUrlToAll() {
      try {
        // 이미 OTA 진행 중이면 중복 요청 방지
        if (this.isApplying) {
          console.log('OTA 이미 진행 중입니다. (ALL)');
          return;
        }

        const otaUrl = typeof this.selectedOtaUrl === 'string'
          ? this.selectedOtaUrl
          : (this.selectedOtaUrl?.url || this.selectedOtaUrl);

        if (!otaUrl) {
          alert('OTA URL을 먼저 선택해주세요.');
          return;
        }

        const company = localStorage.getItem('company');
        if (!company) {
          alert('company 정보가 없습니다. 다시 로그인 후 시도해주세요.');
          return;
        }

        const confirmAll = window.confirm(
          `회사 ${company} 에 연결된 모든 게이트웨이에\n` +
          `다음 OTA URL로 업데이트를 시작합니다.\n\n${otaUrl}\n\n계속하시겠습니까?`
        );
        if (!confirmAll) {
          return;
        }

        this.isApplying = true;
        this.isPaused = true;

        const response = await axios.post('/api/gateway/cmdOTAAll', {
          company,
          selectedOtaUrl: otaUrl
        });

        console.log('cmdOTAAll HTTP response:', response.data);

        if (!response.data.success) {
          alert('OTA ALL 명령 전송 실패: ' + (response.data.message || '알 수 없는 오류'));
        } else {
          const details = response.data.details || {};
          alert(
            `OTA ALL 명령 전송 완료\n` +
            `대상 게이트웨이: ${details.targetCount ?? '?'}개\n` +
            `성공한 전송: ${details.sentCount ?? '?'}개`
          );
        }

        // 즉시 상태 초기화 (개별 게이트웨이 응답을 기다리지 않음)
        this.isApplying = false;
        this.isPaused = false;

        // 최신 상태 반영을 위해 목록 새로고침
        await this.fetchMainGatewayList();
      } catch (error) {
        console.error('OTA ALL apply fail:', error);
        alert('OTA ALL 명령 전송 중 오류가 발생했습니다: ' + error.message);
        this.isApplying = false;
        this.isPaused = false;
      }
    },
    async startBatchUpdateFw0304() {
      const targets = this.gatewaysFw0304;
      if (targets.length === 0) {
        alert('펌웨어 0.0.0.3인 연결된 게이트웨이가 없습니다.');
        return;
      }
      const otaUrl = this.currentOtaUrlForCheck;
      if (!otaUrl) {
        alert('OTA URL을 먼저 선택해주세요. (모달에서 OTA 서버 선택)');
        return;
      }
      const confirmed = window.confirm(
        `펌웨어 0.0.0.3 → 0.0.0.4 일괄 업데이트\n\n` +
        `대상: ${targets.length}개 게이트웨이\n` +
        `간격: 1분에 1개씩 (서버/게이트웨이 부하 완화)\n\n` +
        `계속하시겠습니까?`
      );
      if (!confirmed) return;

      this.batchUpdateInProgress = true;
      this.batchUpdateCancel = false;
      this.isPaused = true;

      const total = targets.length;
      let done = 0;
      let failed = 0;

      for (let i = 0; i < targets.length; i++) {
        if (this.batchUpdateCancel) {
          this.batchUpdateProgress = `중지됨 (${done}/${total})`;
          break;
        }

        const gw = targets[i];
        this.batchUpdateProgress = `${i + 1}/${total} ${gw.devicename || gw.gw_mac}`;

        try {
          await axios.post('/api/gateway/setOTA', {
            selectedOtaUrl: otaUrl,
            ip: gw.ip,
            port: gw.port
          });
          await new Promise(r => setTimeout(r, 1000));

          await axios.post('/api/gateway/cmdOTA', {
            selectedOtaUrl: otaUrl,
            ip: gw.ip,
            port: gw.port
          });
          done++;
        } catch (err) {
          console.error('Batch OTA fail for', gw.gw_mac, err);
          failed++;
        }

        if (i < targets.length - 1 && !this.batchUpdateCancel) {
          await new Promise(r => setTimeout(r, 60000));
        }
      }

      this.batchUpdateInProgress = false;
      this.isPaused = false;
      this.batchUpdateProgress = '';
      await this.fetchMainGatewayList();

      alert(
        `일괄 업데이트 완료\n성공: ${done}개, 실패: ${failed}개` +
        (this.batchUpdateCancel ? '\n(사용자에 의해 중지됨)' : '')
      );
    },
    async applyReportInterval() {
      if (this.isApplying) return;
      this.isApplying = true;
      const reportInterval = this.editingGateway.reportInterval;
      console.log('reportInterval:', reportInterval);

      // WS 응답 타임아웃 — 8초 안에 setReportInterval 응답이 안 오면 stuck 해제
      this.reportIntervalTimeout = setTimeout(() => {
        if (this.isApplying) {
          this.isApplying = false;
          alert('게이트웨이 응답을 받지 못했습니다. GW 가 WS 에 연결되어 있는지 확인해주세요.');
        }
        this.reportIntervalTimeout = null;
      }, 8000);

      try {
        const response = await axios.post('/api/gateway/setReportInterval', {
          id: `${this.editingGateway.ip}:${this.editingGateway.port}`,
          ip: this.editingGateway.ip,
          port: this.editingGateway.port,
          reportInterval: reportInterval
        });
        console.log('Report Interval request sent:', response.data);
        // WS 응답 대기 — onmessage 'setReportInterval' 가 isApplying 해제 + 모달 닫음
      } catch (error) {
        if (this.reportIntervalTimeout) {
          clearTimeout(this.reportIntervalTimeout);
          this.reportIntervalTimeout = null;
        }
        this.isApplying = false;
        const msg = error.response?.data?.message || error.message || '명령 전송 실패';
        alert(`Report Interval apply 실패: ${msg}`);
        console.error('Report Interval apply fail:', error);
      }
    },
    async applyRssiFilter() {
      if (this.isApplying) return;
      this.isApplying = true;
      // RSSI 필터 값의 부호를 반전
      const filter = -this.editingGateway.rssiFilter;

      // WS 응답 타임아웃 — 8초 안에 setRssiFilter 응답이 안 오면 stuck 해제
      this.rssiApplyTimeout = setTimeout(() => {
        if (this.isApplying) {
          this.isApplying = false;
          alert('게이트웨이 응답을 받지 못했습니다. GW 가 WS 에 연결되어 있는지 확인해주세요.');
        }
        this.rssiApplyTimeout = null;
      }, 8000);

      try {
        const response = await axios.post('/api/gateway/setRSSIFilter', {
          id: `${this.editingGateway.ip}:${this.editingGateway.port}`,
          ip: this.editingGateway.ip,
          port: this.editingGateway.port,
          filter: filter
        });
        console.log('RSSI Filter request sent:', filter, response.data);
        // WS 응답 대기 — onmessage 'setRssiFilter' 가 isApplying 해제 + 모달 닫음
      } catch (error) {
        if (this.rssiApplyTimeout) {
          clearTimeout(this.rssiApplyTimeout);
          this.rssiApplyTimeout = null;
        }
        this.isApplying = false;
        const msg = error.response?.data?.message || error.message || '명령 전송 실패';
        alert(`RSSI Filter apply 실패: ${msg}`);
        console.error('RSSI Filter apply fail:', error);
      }
    },
    async applyRssiFilterToAll() {
      if (this.isApplying) return;

      const company = localStorage.getItem('company');
      if (!company) {
        alert('company 정보가 없습니다. 다시 로그인 후 시도해주세요.');
        return;
      }
      const dbm = Number(this.editingGateway.rssiFilter); // 음수 -100 ~ -10
      const filter = -dbm; // 백엔드는 양수 1바이트로 받음

      if (!Number.isInteger(filter) || filter < 10 || filter > 100) {
        alert('RSSI 임계값이 올바르지 않습니다. (-100 ~ -10 dBm 범위)');
        return;
      }

      const confirmAll = window.confirm(
        `회사 ${company} 의 라이브 WS 연결된 모든 GW 에\n` +
        `RSSI 임계값을 ${dbm} dBm 으로 일괄 적용합니다.\n\n계속하시겠습니까?`
      );
      if (!confirmAll) return;

      this.isApplying = true;
      try {
        const response = await axios.post('/api/gateway/setRSSIFilterAll', {
          company,
          filter
        });
        console.log('setRSSIFilterAll response:', response.data);
        const d = response.data?.details || {};
        alert(
          `RSSI 일괄 전송 완료 (${dbm} dBm)\n` +
          `후보 GW: ${d.candidateCount ?? '?'}대, ` +
          `WS 매치: ${d.targetCount ?? '?'}대, ` +
          `전송 성공: ${d.sentCount ?? '?'}대` +
          (d.failed && d.failed.length ? `\n실패: ${d.failed.join(', ')}` : '')
        );
        // 개별 setRssiFilter 와 달리 GW 별 WS 응답이 여러 개 올 수 있어 모달은 그대로 둠.
        // 사용자가 직접 닫게 함.
      } catch (error) {
        const msg = error.response?.data?.message || error.message || '명령 전송 실패';
        alert(`RSSI Filter (ALL) 실패: ${msg}`);
        console.error('setRSSIFilterAll fail:', error);
      } finally {
        this.isApplying = false;
        // 목록 새로고침 — GW 가 응답하며 ws_gw_recent.rssi_filter 갱신
        await this.fetchMainGatewayList();
      }
    },












    async saveGateway() {
      try {
        await this.updateGatewaySettings();
        this.closeEditModal();
        await this.fetchMainGatewayList(); // Refresh list
      } catch (error) {
        console.error('Failed to save gateway settings:', error);
        alert('Failed to save settings.');
      }
    },
    async updateGatewaySettings() {
      const gateway = this.editingGateway;
      
      // Update OTA URL
      await axios.post('/api/gateway/setOTA', {
        selectedOtaUrl: gateway.ota_server_url,
        ip: gateway.ip,
        port: gateway.port
      });

      // Update WS URL
      await axios.post('/api/gateway/setWS', {
        selectedWsUrl: gateway.ws_server_url,
        ip: gateway.ip,
        port: gateway.port
      });

      // Update Report Interval
      await axios.post('/api/gateway/setReportInterval', {
        reportInterval: gateway.report_interval,
        ip: gateway.ip,
        port: gateway.port
      });

      // Update RSSI Filter
      await axios.post('/api/gateway/setRSSIFilter', {
        filter: gateway.rssi_filter,
        ip: gateway.ip,
        port: gateway.port
      });
    },
    sortGateways(key) {
      this.sortOrder = this.sortKey === key ? -this.sortOrder : 1;
      this.sortKey = key;
      
      this.connectedGateways.sort((a, b) => {
        let aValue = a[key];
        let bValue = b[key];
        
        // null 체크
        if (aValue === null) aValue = '';
        if (bValue === null) bValue = '';
        
        // 숫자 비교
        if (typeof aValue === 'number' && typeof bValue === 'number') {
          return (aValue - bValue) * this.sortOrder;
        }
        
        // 문자열 비교
        return String(aValue).localeCompare(String(bValue)) * this.sortOrder;
      });
    },

    getArrowClass(key) {
      if (this.sortKey === key) {
        return this.sortOrder === 1 ? 'arrow-up' : 'arrow-down';
      }
      return 'arrow-default';
    }
  }
}
</script>

<style scoped>
.gateway-list {
  padding: 20px;
}

.header-container {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;
}

.header-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
}

.server-status-bar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  padding: 6px 10px;
  background: #f8f9fa;
  border-radius: 6px;
  border: 1px solid #e0e0e0;
}

.status-label {
  font-size: 0.9em;
  color: #555;
  font-weight: 500;
}

.status-badge {
  font-size: 0.85em;
  padding: 4px 8px;
  border-radius: 4px;
  font-weight: 500;
}

.status-badge.ok,
.status-badge.ws-ok {
  background: #e8f5e9;
  color: #2e7d32;
}

.status-badge.error,
.status-badge.ws-error {
  background: #ffebee;
  color: #c62828;
}

.status-badge.checking {
  background: #fff3e0;
  color: #e65100;
}

.status-check-btn {
  padding: 4px 10px;
  font-size: 0.85em;
  background: #f5f5f5;
  border: 1px solid #ddd;
  border-radius: 4px;
  cursor: pointer;
}

.status-check-btn:hover:not(:disabled) {
  background: #eeeeee;
  border-color: #bbb;
}

.status-check-btn:disabled {
  cursor: not-allowed;
  opacity: 0.7;
}

.last-check {
  font-size: 0.8em;
  color: #777;
}

.refresh-btn {
  padding: 8px 16px;
  background-color: #4CAF50;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
}

.refresh-icon {
  font-size: 16px;
}

.batch-update-btn {
  padding: 8px 16px;
  background-color: #2196F3;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  white-space: nowrap;
}
.batch-update-btn:hover:not(:disabled) {
  background-color: #1976D2;
}
.batch-update-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.batch-cancel-btn {
  padding: 8px 16px;
  background-color: #f44336;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}
.batch-cancel-btn:hover {
  background-color: #d32f2f;
}

.table-container {
  overflow-x: auto;
}

table {
  width: 100%;
  border-collapse: collapse;
  background-color: white;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
}

th, td {
  padding: 12px;
  text-align: left;
  border-bottom: 1px solid #ddd;
}

th {
  background-color: #f5f5f5;
  font-weight: bold;
  cursor: pointer;
  position: relative;
}

th .arrow-default::after {
  content: '▼';
  opacity: 0.3;
}

th .arrow-up::after {
  content: '▲';
}

th .arrow-down::after {
  content: '▼';
}

th .arrow-default,
th .arrow-up,
th .arrow-down {
  display: inline-block;
  margin-left: 5px;
  font-size: 0.8em;
}

th.active {
  background-color: #f0f0f5;
}

th:hover {
  background-color: #f5f5f5;
}

tr:hover {
  background-color: #f9f9f9;
}

.edit-btn {
  padding: 6px 12px;
  background-color: #2196F3;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}
/* admin 이 WS 미연결 GW 에 강제 진입하는 경우 — 시각적으로 구분 (주의 색) */
.edit-btn.edit-btn-warn {
  background-color: #FF9800;
}
.edit-btn.edit-btn-warn:hover {
  background-color: #F57C00;
}
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal-content {
  background: white;
  border-radius: 8px;
  width: 800px;
  max-width: 90%;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.5rem;
  border-bottom: 1px solid #eee;
}

.modal-header h3 {
  margin: 0;
  color: #2c3e50;
  font-size: 1.25rem;
}

.close-button {
  font-size: 1.5rem;
  cursor: pointer;
  color: #666;
  &:hover {
    color: #000;
  }
}

.modal-body {
  padding: 1.5rem;
}

.form-group {
  margin-bottom: 1.5rem;
}

.form-group label {
  display: block;
  margin-bottom: 0.5rem;
  color: #2c3e50;
  font-weight: 500;
}

.input-group {
  display: flex;
  gap: 0.5rem;
}

.setting-select,
.setting-input {
  flex: 1;
  padding: 0.5rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 0.9rem;
}

.ota-check-btn {
  padding: 0.5rem 0.75rem;
  font-size: 0.9em;
  background: #2196F3;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  white-space: nowrap;
}
.ota-check-btn:hover:not(:disabled) {
  background: #1976D2;
}
.ota-check-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
.ota-check-result {
  margin-top: 6px;
  font-size: 0.9em;
  padding: 4px 8px;
  border-radius: 4px;
}
.ota-check-result.ok {
  background: #e8f5e9;
  color: #2e7d32;
}
.ota-check-result.fail {
  background: #ffebee;
  color: #c62828;
}

.apply-btn {
  padding: 0.5rem 1rem;
  background-color: #4CAF50;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.2s;
  
  &:hover {
    background-color: #45a049;
  }
}

.modal-footer {
  padding: 1rem 1.5rem;
  border-top: 1px solid #eee;
  text-align: right;
}

.cancel-btn {
  padding: 0.5rem 1.5rem;
  background-color: #6c757d;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.2s;
  
  &:hover {
    background-color: #5a6268;
  }
}

.connected {
  color: #4CAF50;
}
.disconnected {
  color: #f44336;
}
.disconnected-text {
  color: #999;
  font-size: 0.9em;
  font-style: italic;
}
small {
  font-size: 0.8em;
  margin-left: 5px;
  color: #666;
}
.controls {
  display: flex;
  justify-content: center;  /* 가운데 정렬 */
  align-items: center;
  margin-bottom: 30px;
}

.update-interval-container {
  display: flex;
  align-items: center;
  gap: 10px;  /* 라벨과 입력 필드 사이 간격 */
}

#updateInterval {
  width: 100px;  /* 입력 필드 너비 */
  padding: 5px;
  border: 1px solid #ddd;
  border-radius: 4px;
}
.slider-container {
  margin: 20px 0;
}

.slider {
  width: 100%;
  height: 8px;
  -webkit-appearance: none;
  appearance: none;
  background: #ddd;
  border-radius: 4px;
  outline: none;
}

.slider::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 20px;
  height: 20px;
  background: #4CAF50;
  border-radius: 50%;
  cursor: pointer;
}

.slider::-moz-range-thumb {
  width: 20px;
  height: 20px;
  background: #4CAF50;
  border-radius: 50%;
  cursor: pointer;
}

.slider-value {
  text-align: center;
  margin-top: 10px;
  font-size: 1.2em;
  font-weight: bold;
  color: #333;
}

.slider-labels {
  display: flex;
  justify-content: space-between;
  margin-top: 5px;
  color: #666;
  font-size: 0.9em;
}
.rssi-value {
  text-align: center;
  margin: 10px 0;
}

.rssi-range {
  display: flex;
  justify-content: space-between;
  margin: 5px 0;
}

.rssi-description {
  font-size: 0.9em;
  color: #666;
  margin-top: 5px;
}
.rssi-container {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.rssi-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 10px;
}
.rssi-actions .apply-btn { margin-top: 0; }
.apply-all-btn {
  background-color: #2196F3;
}
.apply-all-btn:hover:not(:disabled) {
  background-color: #1976D2;
}

.rssi-slider {
  width: 100%;
  margin: 10px 0;
  direction: rtl;
}

.rssi-labels {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  margin-top: -5px;
}

.rssi-min, .rssi-max {
  color: #666;
  font-size: 0.9em;
}

.current-value {
  color: #333;
  font-weight: bold;
}

.apply-btn {
  align-self: flex-end;
  margin-top: 10px;
}
.circle-slider-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 350px;
  position: relative;
}

.circle-visualization {
  border-radius: 50%;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
}

.current-value {
  font-size: 1.2em;
  font-weight: bold;
  color: #2196F3;
}

.circle-range {
  direction: rtl;
  position: absolute;
  bottom: 20px;
  width: 80%;
  margin: 0 auto;
}

.rssi-labels {
  display: flex;
  justify-content: space-between;
  width: 80%;
  margin: 10px auto;
  color: #666;
}

.apply-btn {
  margin-top: 20px;
  padding: 8px 16px;
  background-color: #4CAF50;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}
.rssi-min {
  color: #666;
  font-size: 0.9em;
}

.rssi-max {
  color: #666;
  font-size: 0.9em;
}
.apply-btn:hover {
  background-color: #45a049;
}
.range-description {
  text-align: center;
  margin-top: 10px;
  color: #666;
  font-size: 0.9em;
}
</style>